import React from "react";
import cns from "classnames";
import { transparentize } from "polished";
import { CheckboxBlankCircleOutline } from "mdi-material-ui";
import { Checkbox, Grid, makeStyles } from "@material-ui/core";
import {
  ConsultantQualityCheckDataFragment,
  ConsultantQuality_ConsultantNewFragment
} from "@graphql";
import { toASCII } from "@bonliva-crm/common";
import locale from "@bonliva-crm/locale";
import { Label } from "@components/typography/Label";
import { WithTooltip } from "@common";
import { statusIcon, statusIconColor } from "./utils";
import { QualityCheckStatusSetOn } from "./quality-check-status-set-on";
import QualityCheckStatusExpiresOn from "./quality-check-status-expires-on";

export type QualityCheckProps = {
  active?: boolean;
  className?: string;
  consultantData: ConsultantQuality_ConsultantNewFragment;
  data: ConsultantQualityCheckDataFragment;
  disabled: boolean;
  disabledTitle?: string;
  notClickable?: boolean;
  onClick?: (e: React.SyntheticEvent<any>) => any;
  renderLabel?: (label: React.ReactNode) => React.ReactNode;
  renderExtraAction?: () => React.ReactNode;
  renderExtraLabels?: () => React.ReactNode;
  showComment?: boolean;
  showExpiresOn?: boolean;
  withDefaultStatuses?: boolean;
};

export function QualityCheck(props: QualityCheckProps) {
  const {
    className,
    data,
    disabled,
    disabledTitle,
    onClick,
    showComment,
    showExpiresOn = true,
    withDefaultStatuses = true,
    notClickable = false
  } = props;

  const classes = useStyles();

  function renderLabel() {
    const { data, renderLabel } = props;
    const textDecoration = data.Disabled || data.HasExpired ? "line-through" : "initial";
    const label = <Label style={{ textDecoration }}>{data.Name}</Label>;

    return renderLabel ? renderLabel(label) : label;
  }

  function renderCheckbox() {
    const CheckedIcon = statusIcon(data);
    const status = data?.Status;
    const checked = Boolean(status);
    const title = status?.Name && locale.qualityStatuses[status.Name];
    const color = statusIconColor(data);

    return (
      <WithTooltip title={disabledTitle || title}>
        <span>
          <Checkbox
            checked={checked}
            checkedIcon={<CheckedIcon htmlColor={color} />}
            icon={<CheckboxBlankCircleOutline style={{ opacity: 0.2 }} />}
            className={classes.checkbox}
            disabled={data.Disabled || disabled}
            id={data._id}
          />
        </span>
      </WithTooltip>
    );
  }

  const statuses = [
    withDefaultStatuses && data.StatusSetBy && data.Status && (
      <QualityCheckStatusSetOn data={data} showComment={showComment} />
    ),
    withDefaultStatuses && showExpiresOn && data.ExpiresOn && (
      <QualityCheckStatusExpiresOn data={data} />
    ),
    props.renderExtraLabels && props.renderExtraLabels()
  ].filter(Boolean);

  return (
    <Grid
      className={cns(
        classes.root,
        {
          [classes.active]: props.active,
          [classes.disabled]: props.disabled,
          [classes.expired]: data.HasExpired,
          [classes.expiresSoon]: data.ExpiresSoon
        },
        className
      )}
      id={toASCII(`quality_check_${data.Country}_${data.Name}_non_national`)}
      data-test="ConsultantQualityCheck"
      data-test-id={data._id}
      data-test-name={data.Name}
      onClick={onClick ? (e) => !notClickable && !disabled && onClick(e) : undefined}
    >
      <div className={classes.checkboxWrap}>{renderCheckbox()}</div>
      <div>
        <div className={classes.label}>
          {renderLabel()}

          <div className={classes.actions}>
            {props.renderExtraAction && props.renderExtraAction()}
          </div>
        </div>

        {statuses.length ? <div className={classes.statuses}>{statuses}</div> : null}
      </div>
    </Grid>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "flex-start",
    padding: 16,
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    "&:not($disabled)": {
      transition: theme.transitions.create("background-color", { duration: 250 }),
      cursor: "pointer",
      "&:hover": {
        backgroundColor: transparentize(0.85, theme.palette.primary.main)
      }
    },
    "& > div:nth-child(2)": {
      flex: "1 0 0"
    }
  },
  active: {
    backgroundColor: theme.palette.grey[200]
  },
  disabled: { opacity: 0.6 },
  expired: { background: transparentize(0.75, theme.palette.primary.main) },
  expiresSoon: { background: transparentize(0.75, theme.palette.primary.main) },
  checkboxWrap: {},
  checkbox: {
    height: "min-content",
    width: "min-content",
    marginRight: theme.spacing(2),
    padding: 0
  },
  checkboxWarning: {
    color: [theme.palette.primary.main, "!important"].join(" ")
  },
  sithsCardNumberComment: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start"
  },
  sithsCardNumberCopy: {
    marginLeft: 5,
    fontSize: 14,
    color: theme.palette.primary.main
  },
  label: { display: "flex", alignItems: "center", minHeight: 24 },
  actions: { marginLeft: "auto", display: "flex", alignItems: "center" },
  statuses: {
    marginTop: 16
  }
}));
